import React, { useState } from 'react';
import RebalancingSummaryCards from './RebalancingSummaryCards';

// Sleeve Allocation Visualization Component
const SleeveAllocationVisualization = ({ sleeveAllocations, colorMap, hideDollarValues = false }) => {
    const [isSectionCollapsed, setIsSectionCollapsed] = useState(false);

    if (!sleeveAllocations || !Array.isArray(sleeveAllocations) || sleeveAllocations.length === 0) {
        return (
            <div className="text-center py-3 text-muted">
                <i className="bi bi-collection me-2"></i>
                No sleeve allocation data available
            </div>
        );
    }

    // Format currency helper
    const formatCurrency = (value) => {
        return hideDollarValues ? '$***' : `$${value?.toLocaleString() || '0'}`;
    };

    // Prepare data for all three views with consistent ordering by target weight
    const allocationData = sleeveAllocations
        .map(sleeve => ({
            name: sleeve.name || sleeve.sleeveName || 'Unknown',
            target: {
                weight: sleeve.targetWeight || sleeve.target_weight || 0,
                value: sleeve.targetValue || sleeve.target_value || 0
            },
            before: {
                weight: sleeve.beforeWeight || sleeve.before_weight || sleeve.actualWeight || sleeve.actual_weight || 0,
                value: sleeve.beforeValue || sleeve.before_value || sleeve.actualValue || sleeve.actual_value || 0
            },
            after: {
                weight: sleeve.afterWeight || sleeve.after_weight || sleeve.projectedWeight || sleeve.projected_weight || 0,
                value: sleeve.afterValue || sleeve.after_value || sleeve.projectedValue || sleeve.projected_value || 0
            },
            tradeImpact: sleeve.tradeImpact || sleeve.trade_impact || 0
        }))
        .sort((a, b) => b.target.weight - a.target.weight);

    // Helper function to create progress bar
    const createProgressBar = (data, title, subtitle) => (
        <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted fw-bold">{title}</small>
                {subtitle && <small className="text-muted">{subtitle}</small>}
            </div>
            <div className="progress" style={{ height: '25px' }}>
                {data.map((sleeve, index) => {
                    const color = colorMap[sleeve.name];
                    return (
                        <div
                            key={index}
                            className="progress-bar"
                            role="progressbar"
                            style={{
                                width: `${sleeve.weight}%`,
                                backgroundColor: color
                            }}
                            aria-valuenow={sleeve.weight}
                            aria-valuemin="0"
                            aria-valuemax="100"
                            title={`${sleeve.name}: ${sleeve.weight.toFixed(1)}% (${formatCurrency(sleeve.value)})`}
                        >
                            {sleeve.weight > 8 ? `${sleeve.weight.toFixed(1)}%` : ''}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="mb-4 border rounded">
            <div
                className="card-header bg-light d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
                onClick={() => setIsSectionCollapsed(v => !v)}
            >
                <div className="d-flex align-items-center">
                    <i className={`bi ${isSectionCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'} me-2`}></i>
                    <h6 className="mb-0">
                        <i className="bi bi-collection me-2"></i>
                        Group Allocation Comparison - By Sleeve ({allocationData.length} sleeves)
                    </h6>
                </div>
            </div>

            {!isSectionCollapsed && (
                <div className="p-3">
                    {/* Before Rebalance */}
                    {createProgressBar(
                        allocationData.map(sleeve => ({ name: sleeve.name, weight: sleeve.before.weight, value: sleeve.before.value })),
                        'Before Rebalance',
                        'Current allocation'
                    )}

                    {/* After Rebalance */}
                    {createProgressBar(
                        allocationData.map(sleeve => ({ name: sleeve.name, weight: sleeve.after.weight, value: sleeve.after.value })),
                        'After Rebalance',
                        'Projected allocation'
                    )}

                    {/* Target Allocation */}
                    {createProgressBar(
                        allocationData.map(sleeve => ({ name: sleeve.name, weight: sleeve.target.weight, value: sleeve.target.value })),
                        'Target Allocation',
                        'Goal allocation'
                    )}

                    {/* Legend */}
                    <div className="mt-3">
                        <small className="text-muted fw-bold mb-2 d-block">Legend</small>
                        <div className="d-flex flex-wrap">
                            {allocationData.map((sleeve, index) => {
                                const color = colorMap[sleeve.name];
                                const hasChange = Math.abs(sleeve.after.weight - sleeve.before.weight) > 0.1;
                                const changeDirection = sleeve.after.weight > sleeve.before.weight ? 'up' : 'down';
                                const changeAmount = Math.abs(sleeve.after.weight - sleeve.before.weight);

                                const displayName = sleeve.name.length > 20 ? sleeve.name.substring(0, 20) + '...' : sleeve.name;

                                return (
                                    <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-3" style={{ maxWidth: '20%', flexBasis: '20%' }}>
                                        <div className="d-flex align-items-center mb-2">
                                            <span
                                                className="badge me-2 flex-shrink-0"
                                                style={{ backgroundColor: color, width: '12px', height: '12px' }}
                                            >&nbsp;</span>
                                            <div className="min-w-0">
                                                <span
                                                    className="fw-medium d-block text-truncate"
                                                    title={sleeve.name.length > 20 ? sleeve.name : undefined}
                                                    style={{ cursor: sleeve.name.length > 20 ? 'help' : 'default' }}
                                                >
                                                    {displayName}
                                                </span>
                                                <div className="small text-muted">
                                                    Target: {sleeve.target.weight.toFixed(1)}%
                                                    {hasChange && (
                                                        <span className={`ms-1 ${changeDirection === 'up' ? 'text-success' : 'text-danger'}`}>
                                                            <i className={`bi bi-arrow-${changeDirection} me-1`}></i>
                                                            {changeAmount.toFixed(1)}%
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Securities Allocation Visualization Component
const SecuritiesAllocationVisualization = ({ sleeveAllocations, colorMap, hideDollarValues = false }) => {
    const [isSectionCollapsed, setIsSectionCollapsed] = useState(false);

    if (!sleeveAllocations || !Array.isArray(sleeveAllocations) || sleeveAllocations.length === 0) {
        return (
            <div className="text-center py-3 text-muted">
                <i className="bi bi-pie-chart me-2"></i>
                No securities allocation data available
            </div>
        );
    }

    // Format currency helper
    const formatCurrency = (value) => {
        return hideDollarValues ? '$***' : `$${value?.toLocaleString() || '0'}`;
    };

    // Prepare data for all three views with consistent ordering by target weight
    const allocationData = sleeveAllocations
        .map(sleeve => ({
            name: sleeve.name || sleeve.sleeveName || 'Unknown',
            target: {
                weight: sleeve.targetWeight || sleeve.target_weight || 0,
                value: sleeve.targetValue || sleeve.target_value || 0
            },
            before: {
                weight: sleeve.beforeWeight || sleeve.before_weight || sleeve.actualWeight || sleeve.actual_weight || 0,
                value: sleeve.beforeValue || sleeve.before_value || sleeve.actualValue || sleeve.actual_value || 0
            },
            after: {
                weight: sleeve.afterWeight || sleeve.after_weight || sleeve.projectedWeight || sleeve.projected_weight || 0,
                value: sleeve.afterValue || sleeve.after_value || sleeve.projectedValue || sleeve.projected_value || 0
            },
            tradeImpact: sleeve.tradeImpact || sleeve.trade_impact || 0
        }))
        .sort((a, b) => b.target.weight - a.target.weight);

    // Helper function to create progress bar
    const createProgressBar = (data, title, subtitle) => (
        <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted fw-bold">{title}</small>
                {subtitle && <small className="text-muted">{subtitle}</small>}
            </div>
            <div className="progress" style={{ height: '25px' }}>
                {data.map((sleeve, index) => {
                    const color = colorMap[sleeve.name];
                    return (
                        <div
                            key={index}
                            className="progress-bar"
                            role="progressbar"
                            style={{
                                width: `${sleeve.weight}%`,
                                backgroundColor: color
                            }}
                            aria-valuenow={sleeve.weight}
                            aria-valuemin="0"
                            aria-valuemax="100"
                            title={`${sleeve.name}: ${sleeve.weight.toFixed(1)}% (${formatCurrency(sleeve.value)})`}
                        >
                            {sleeve.weight > 8 ? `${sleeve.weight.toFixed(1)}%` : ''}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="mb-4 border rounded">
            <div
                className="card-header bg-light d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
                onClick={() => setIsSectionCollapsed(v => !v)}
            >
                <div className="d-flex align-items-center">
                    <i className={`bi ${isSectionCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'} me-2`}></i>
                    <h6 className="mb-0">
                        <i className="bi bi-pie-chart me-2"></i>
                        Group Allocation Comparison - By Security ({allocationData.length} securities)
                    </h6>
                </div>
            </div>

            {!isSectionCollapsed && (
                <div className="p-3">
                    {/* Before Rebalance */}
                    {createProgressBar(
                        allocationData.map(sleeve => ({ name: sleeve.name, weight: sleeve.before.weight, value: sleeve.before.value })),
                        'Before Rebalance',
                        'Current allocation'
                    )}

                    {/* After Rebalance */}
                    {createProgressBar(
                        allocationData.map(sleeve => ({ name: sleeve.name, weight: sleeve.after.weight, value: sleeve.after.value })),
                        'After Rebalance',
                        'Projected allocation'
                    )}

                    {/* Target Allocation */}
                    {createProgressBar(
                        allocationData.map(sleeve => ({ name: sleeve.name, weight: sleeve.target.weight, value: sleeve.target.value })),
                        'Target Allocation',
                        'Goal allocation'
                    )}

                    {/* Legend */}
                    <div className="mt-3">
                        <small className="text-muted fw-bold mb-2 d-block">Legend</small>
                        <div className="d-flex flex-wrap">
                            {allocationData.map((sleeve, index) => {
                                const color = colorMap[sleeve.name];
                                const hasChange = Math.abs(sleeve.after.weight - sleeve.before.weight) > 0.1;
                                const changeDirection = sleeve.after.weight > sleeve.before.weight ? 'up' : 'down';
                                const changeAmount = Math.abs(sleeve.after.weight - sleeve.before.weight);

                                const displayName = sleeve.name.length > 20 ? sleeve.name.substring(0, 20) + '...' : sleeve.name;

                                return (
                                    <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-3" style={{ maxWidth: '20%', flexBasis: '20%' }}>
                                        <div className="d-flex align-items-center mb-2">
                                            <span
                                                className="badge me-2 flex-shrink-0"
                                                style={{ backgroundColor: color, width: '12px', height: '12px' }}
                                            >&nbsp;</span>
                                            <div className="min-w-0">
                                                <span
                                                    className="fw-medium d-block text-truncate"
                                                    title={sleeve.name.length > 20 ? sleeve.name : undefined}
                                                    style={{ cursor: sleeve.name.length > 20 ? 'help' : 'default' }}
                                                >
                                                    {displayName}
                                                </span>
                                                <div className="small text-muted">
                                                    Target: {sleeve.target.weight.toFixed(1)}%
                                                    {hasChange && (
                                                        <span className={`ms-1 ${changeDirection === 'up' ? 'text-success' : 'text-danger'}`}>
                                                            <i className={`bi bi-arrow-${changeDirection} me-1`}></i>
                                                            {changeAmount.toFixed(1)}%
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Account Allocation Visualization Component
const AccountAllocationVisualization = ({ accountAllocations, accountSleeveAllocations, colorMap, hideDollarValues = false, viewType = 'security' }) => {
    // State to track which account panels are collapsed
    const [collapsedAccounts, setCollapsedAccounts] = useState({});
    // State to track if the entire section is collapsed
    const [isSectionCollapsed, setIsSectionCollapsed] = useState(false);

    // Choose the appropriate data source based on viewType
    const allocationsData = viewType === 'sleeve' ? accountSleeveAllocations : accountAllocations;

    // Initialize collapsed state for accounts with 0 trades
    React.useEffect(() => {
        if (allocationsData && Array.isArray(allocationsData)) {
            const initialCollapsedState = {};
            allocationsData.forEach((accountData, index) => {
                // Collapse by default if tradesCount is 0
                initialCollapsedState[index] = (accountData.tradesCount || 0) === 0;
            });
            setCollapsedAccounts(initialCollapsedState);
        }
    }, [allocationsData]);

    if (!allocationsData || !Array.isArray(allocationsData) || allocationsData.length === 0) {
        return (
            <div className="text-center py-3 text-muted">
                <i className="bi bi-building me-2"></i>
                No account allocation data available
            </div>
        );
    }

    // Toggle collapse state for a specific account
    const toggleAccountCollapse = (accountIndex) => {
        setCollapsedAccounts(prev => ({
            ...prev,
            [accountIndex]: !prev[accountIndex]
        }));
    };

    // Format currency helper
    const formatCurrency = (value) => {
        return hideDollarValues ? '$***' : `$${value?.toLocaleString() || '0'}`;
    };

    // Helper function to create progress bar for an account
    const createAccountProgressBar = (accountName, accountData, progressViewType, title) => {
        const dataKey = viewType === 'sleeve' ? 'sleeves' : 'securities';
        const items = accountData[dataKey] || [];

        if (items.length === 0) {
            return null;
        }

        const itemData = items.map(item => ({
            name: item.name,
            weight: item[progressViewType]?.weight || 0,
            value: item[progressViewType]?.value || 0
        }));

        if (itemData.length === 0) {
            return null;
        }

        return (
            <div className="mb-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <small className="text-muted">{title}</small>
                    <small className="text-muted">Total: {formatCurrency(accountData.totalValue)}</small>
                </div>
                <div className="progress" style={{ height: '20px' }}>
                    {itemData.map((item, index) => {
                        const color = colorMap[item.name];
                        return (
                            <div
                                key={index}
                                className="progress-bar"
                                role="progressbar"
                                style={{
                                    width: `${item.weight}%`,
                                    backgroundColor: color
                                }}
                                aria-valuenow={item.weight}
                                aria-valuemin="0"
                                aria-valuemax="100"
                                title={`${item.name}: ${item.weight.toFixed(1)}% (${formatCurrency(item.value)})`}
                            >
                                {item.weight > 10 ? `${item.weight.toFixed(1)}%` : ''}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="mb-4 border rounded">
            <div
                className="card-header bg-light d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
                onClick={() => setIsSectionCollapsed(v => !v)}
            >
                <div className="d-flex align-items-center">
                    <i className={`bi ${isSectionCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'} me-2`}></i>
                    <h6 className="mb-0">
                        <i className="bi bi-building me-2"></i>
                        Account Allocation Comparison - By {viewType === 'sleeve' ? 'Sleeve' : 'Security'} ({allocationsData.length} accounts)
                    </h6>
                </div>
            </div>

            {!isSectionCollapsed && (
                <div className="p-3">
                    {allocationsData.map((accountData, accountIndex) => {
                        const isCollapsed = collapsedAccounts[accountIndex] || false;
                        const hasTrades = (accountData.tradesCount || 0) > 0;

                        return (
                            <div key={accountIndex} className="card mb-3">
                                <div
                                    className="card-header bg-light"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => toggleAccountCollapse(accountIndex)}
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            <i className={`bi ${isCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'} me-2`}></i>
                                            <h6 className="mb-0">
                                                {accountData.accountName} ({accountData.accountNumber})
                                            </h6>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <span className="badge bg-secondary me-2">{accountData.accountType}</span>
                                            <span className={`badge me-2 ${hasTrades ? 'bg-success' : 'bg-warning'}`}>
                                                {accountData.tradesCount} trades
                                            </span>
                                            <small className="text-muted">
                                                {formatCurrency(accountData.totalValue)}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                                {!isCollapsed && (
                                    <div className="card-body">
                                        {/* Before Rebalance */}
                                        {createAccountProgressBar(
                                            accountData.accountName,
                                            accountData,
                                            'before',
                                            'Before Rebalance'
                                        )}

                                        {/* After Rebalance */}
                                        {createAccountProgressBar(
                                            accountData.accountName,
                                            accountData,
                                            'after',
                                            'After Rebalance'
                                        )}

                                        {/* Legend for this account */}
                                        <div className="mt-3">
                                            <small className="text-muted fw-bold mb-2 d-block">Legend</small>
                                            <div className="d-flex flex-wrap">
                                                {(accountData[viewType === 'sleeve' ? 'sleeves' : 'securities'] || [])
                                                    .filter(item => {
                                                        // Only show items that have non-zero weight in either before or after
                                                        const beforeWeight = item.before?.weight || 0;
                                                        const afterWeight = item.after?.weight || 0;
                                                        return beforeWeight > 0 || afterWeight > 0;
                                                    })
                                                    .map((item, index) => {
                                                        const color = colorMap[item.name];
                                                        const hasChange = Math.abs(item.after.weight - item.before.weight) !== 0.0;
                                                        const changeDirection = item.after.weight > item.before.weight ? 'up' : 'down';
                                                        const changeAmount = Math.abs(item.after.weight - item.before.weight);
                                                        const showAmount = changeAmount >= 0.05; // Only show change amount if it's significant

                                                        const displayName = item.name.length > 20 ? item.name.substring(0, 20) + '...' : item.name;

                                                        return (
                                                            <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-3" style={{ maxWidth: '20%', flexBasis: '20%' }}>
                                                                <div className="d-flex align-items-center mb-2">
                                                                    <span
                                                                        className="badge me-2 flex-shrink-0"
                                                                        style={{ backgroundColor: color, width: '12px', height: '12px' }}
                                                                    >&nbsp;</span>
                                                                    <div className="min-w-0">
                                                                        <span
                                                                            className="fw-medium d-block text-truncate"
                                                                            title={item.name.length > 20 ? item.name : undefined}
                                                                            style={{ cursor: item.name.length > 20 ? 'help' : 'default' }}
                                                                        >
                                                                            {displayName}
                                                                        </span>
                                                                        <div className="small text-muted">
                                                                            {item.before.weight.toFixed(1)}% → {item.after.weight.toFixed(1)}%
                                                                            {hasChange && (
                                                                                <span className={`ms-1 ${changeDirection === 'up' ? 'text-success' : 'text-danger'}`}>
                                                                                    <i className={`bi bi-arrow-${changeDirection} me-1`}></i>
                                                                                    {showAmount && `${changeAmount.toFixed(1)}%`}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const PortfolioRebalanceResultsModal = ({
    show,
    onClose,
    isLoadingPortfolioRebalance,
    portfolioRebalanceError,
    rebalanceResult,
    applyRebalanceTrades,
    operationType = 'portfolio', // Default to portfolio if not provided
    hideDollarValues = false
}) => {
    const [isSummaryCollapsed, setIsSummaryCollapsed] = useState(true);
    const [isRawCollapsed, setIsRawCollapsed] = useState(true);
    const [allocationViewType, setAllocationViewType] = useState('security'); // 'security' or 'sleeve'

    // Format currency helper that respects hideDollarValues
    const formatCurrency = (value) => {
        return hideDollarValues ? '$***' : `$${value?.toLocaleString() || '0'}`;
    };

    // Get operation-specific titles and messages
    const getOperationTitle = () => {
        switch (operationType) {
            case 'cash_reserve':
                return 'Cash Raising Results';
            case 'spring_cleaning':
                return 'Spring Cleaning Results';
            default:
                return 'Portfolio Rebalance Results';
        }
    };

    const getLoadingMessage = () => {
        switch (operationType) {
            case 'cash_reserve':
                return 'Generating cash raising recommendations...';
            case 'spring_cleaning':
                return 'Performing spring cleaning rebalance...';
            default:
                return 'Generating rebalance recommendations...';
        }
    };

    if (!show) return null;

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">                    <div className="modal-header">
                    <h5 className="modal-title">
                        <i className="bi bi-calculator me-2"></i>
                        {getOperationTitle()}
                    </h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={onClose}
                    ></button>
                </div>
                    <div className="modal-body">
                        {isLoadingPortfolioRebalance ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3">{getLoadingMessage()}</p>
                            </div>
                        ) : portfolioRebalanceError ? (
                            <div className="alert alert-danger">
                                <h6>Error</h6>
                                <p className="mb-0">{portfolioRebalanceError}</p>
                            </div>
                        ) : rebalanceResult ? (
                            <div>
                                {/* Summary Cards */}
                                <div className="row mb-4">
                                    <div className="col-md-3">
                                        <div className="card bg-light">
                                            <div className="card-body text-center">
                                                <h6 className="card-title">Total Trades</h6>
                                                <h4 className="text-primary">{rebalanceResult.total_trades}</h4>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="card bg-light">
                                            <div className="card-body text-center">
                                                <h6 className="card-title">Total Trade Value</h6>
                                                <h4 className="text-success">{formatCurrency(rebalanceResult.total_trade_value)}</h4>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="card bg-light">
                                            <div className="card-body text-center">
                                                <h6 className="card-title">Target Achievement</h6>
                                                <h4 className="text-info">{(rebalanceResult.target_achievement * 100).toFixed(1)}%</h4>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="card bg-light">
                                            <div className="card-body text-center">
                                                <h6 className="card-title">Violations</h6>
                                                <h4 className={`${(rebalanceResult.cash_violations?.length || 0) + (rebalanceResult.restriction_violations?.length || 0) > 0 ? 'text-warning' : 'text-success'}`}>
                                                    {(rebalanceResult.cash_violations?.length || 0) + (rebalanceResult.restriction_violations?.length || 0)}
                                                </h4>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Create unified color map for consistent coloring across all visualizations */}
                                {(() => {
                                    // Collect all unique security/sleeve names from all visualizations
                                    const allNames = new Set();

                                    // From security allocations
                                    if (rebalanceResult.security_allocations && Array.isArray(rebalanceResult.security_allocations)) {
                                        rebalanceResult.security_allocations.forEach(security => {
                                            const name = security.name || security.securityName || 'Unknown';
                                            allNames.add(name);
                                        });
                                    }

                                    // From sleeve allocations
                                    if (rebalanceResult.sleeve_allocations && Array.isArray(rebalanceResult.sleeve_allocations)) {
                                        rebalanceResult.sleeve_allocations.forEach(sleeve => {
                                            const name = sleeve.name || sleeve.sleeveName || 'Unknown';
                                            allNames.add(name);
                                        });
                                    }

                                    // From account allocations
                                    if (rebalanceResult.account_allocations && typeof rebalanceResult.account_allocations === 'object') {
                                        Object.values(rebalanceResult.account_allocations).forEach(accountData => {
                                            if (accountData.securities) {
                                                accountData.securities.forEach(security => {
                                                    if (security.name) {
                                                        allNames.add(security.name);
                                                    }
                                                });
                                            }
                                        });
                                    }

                                    // Create consistent color mapping
                                    const sortedNames = Array.from(allNames).sort();
                                    const baseColors = ['#007bff', '#28a745', '#fd7e14', '#6f42c1', '#20c997', '#e83e8c', '#ffc107', '#dc3545', '#6c757d', '#17a2b8'];
                                    const unifiedColorMap = {};

                                    // Generate unique colors for all securities
                                    const generateUniqueColors = (count) => {
                                        const colors = [...baseColors];

                                        // If we need more colors than our base palette, generate additional unique colors
                                        if (count > baseColors.length) {
                                            const additionalNeeded = count - baseColors.length;

                                            // Generate additional colors using HSL color space for better distribution
                                            for (let i = 0; i < additionalNeeded; i++) {
                                                const hue = (i * 137.5) % 360; // Golden angle for better distribution
                                                const saturation = 65 + (i % 3) * 15; // Vary saturation 65-95%
                                                const lightness = 45 + (i % 4) * 10; // Vary lightness 45-75%
                                                colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
                                            }
                                        }

                                        return colors;
                                    };

                                    const uniqueColors = generateUniqueColors(sortedNames.length);
                                    sortedNames.forEach((name, index) => {
                                        unifiedColorMap[name] = uniqueColors[index];
                                    });

                                    return (
                                        <>
                                            {/* Allocation View Toggle */}
                                            {(rebalanceResult.security_allocations || rebalanceResult.sleeve_allocations || rebalanceResult.account_allocations) && (
                                                <div className="mb-4">
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <h6 className="mb-0">
                                                            <i className="bi bi-bar-chart me-2"></i>
                                                            Allocation Comparison
                                                        </h6>
                                                        <div className="btn-group btn-group-sm" role="group">
                                                            <button
                                                                type="button"
                                                                className={`btn ${allocationViewType === 'security' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                                onClick={() => setAllocationViewType('security')}
                                                                disabled={!rebalanceResult.security_allocations}
                                                            >
                                                                <i className="bi bi-pie-chart me-1"></i>
                                                                By Security
                                                            </button>                                            <button
                                                                type="button"
                                                                className={`btn ${allocationViewType === 'sleeve' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                                onClick={() => setAllocationViewType('sleeve')}
                                                                disabled={!rebalanceResult.sleeve_allocations}
                                                            >
                                                                <i className="bi bi-collection me-1"></i>
                                                                By Sleeve
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Security Allocation Visualization */}
                                            {allocationViewType === 'security' && rebalanceResult.security_allocations && (
                                                <SecuritiesAllocationVisualization
                                                    sleeveAllocations={rebalanceResult.security_allocations}
                                                    colorMap={unifiedColorMap}
                                                    hideDollarValues={hideDollarValues}
                                                />
                                            )}

                                            {/* Sleeve Allocation Visualization */}
                                            {allocationViewType === 'sleeve' && rebalanceResult.sleeve_allocations && (
                                                <SleeveAllocationVisualization
                                                    sleeveAllocations={rebalanceResult.sleeve_allocations}
                                                    colorMap={unifiedColorMap}
                                                    hideDollarValues={hideDollarValues}
                                                />
                                            )}

                                            {/* Account Allocation Visualization - Always show and respect main toggle */}
                                            {(rebalanceResult.account_allocations || rebalanceResult.account_sleeve_allocations) && (
                                                <div className="mt-4">
                                                    <AccountAllocationVisualization
                                                        accountAllocations={rebalanceResult.account_allocations}
                                                        accountSleeveAllocations={rebalanceResult.account_sleeve_allocations}
                                                        colorMap={unifiedColorMap}
                                                        hideDollarValues={hideDollarValues}
                                                        viewType={allocationViewType}
                                                    />
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}

                                {/* Rebalancing Summary */}
                                {rebalanceResult.trades && rebalanceResult.trades.length > 0 && (
                                    <div className="card mb-4">
                                        <div
                                            className="card-header bg-primary text-white d-flex align-items-center"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setIsSummaryCollapsed(v => !v)}
                                        >
                                            <h6 className="mb-0">
                                                <i className={`bi ${isSummaryCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'} me-2`}></i>
                                                Rebalancing Summary & Explanation
                                            </h6>
                                        </div>
                                        {!isSummaryCollapsed && (
                                            <div className="p-3">
                                                <RebalancingSummaryCards
                                                    rebalanceResult={rebalanceResult}
                                                    hideDollarValues={hideDollarValues}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Violations Alerts */}
                                {rebalanceResult.cash_violations && rebalanceResult.cash_violations.length > 0 && (
                                    <div className="alert alert-warning">
                                        <h6>Cash Reserve Violations</h6>
                                        <ul className="mb-0">
                                            {rebalanceResult.cash_violations.map((violation, index) => (
                                                <li key={index}>{violation}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {rebalanceResult.restriction_violations && rebalanceResult.restriction_violations.length > 0 && (
                                    <div className="alert alert-danger">
                                        <h6>Trade Restriction Violations</h6>
                                        <ul className="mb-0">
                                            {rebalanceResult.restriction_violations.map((violation, index) => (
                                                <li key={index}>{violation}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Configuration Used */}
                                {rebalanceResult.config && (
                                    <div className="alert alert-info">
                                        <h6>Configuration Used</h6>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <small>
                                                    <strong>Deviation Threshold:</strong> {(rebalanceResult.config.deviation_threshold * 100).toFixed(1)}%<br />
                                                    <strong>Min Trade Value:</strong> ${rebalanceResult.config.min_trade_value}
                                                </small>
                                            </div>
                                            <div className="col-md-6">
                                                <small>
                                                    <strong>Tax Efficiency Weight:</strong> {(rebalanceResult.config.tax_efficiency_weight * 100).toFixed(0)}%<br />
                                                    <strong>Cash Buffer:</strong> {(rebalanceResult.config.cash_buffer_percentage * 100).toFixed(1)}%
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Raw JSON Response */}
                                <div className="card mt-4">
                                    <div
                                        className="card-header bg-secondary text-white d-flex align-items-center justify-content-between"
                                        style={{ cursor: 'pointer', background: '#6c757d' }} // force bg-secondary color
                                        onClick={() => setIsRawCollapsed(v => !v)}
                                    >
                                        <div className="d-flex align-items-center">
                                            <h6 className="mb-0">
                                                <i className={`bi ${isRawCollapsed ? 'bi-chevron-right' : 'bi-chevron-down'} me-2`}></i>
                                                Raw Response Data
                                            </h6>
                                        </div>
                                        {/* Copy Data button, stop click from toggling collapse */}
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-sm border-0"
                                            style={{ color: 'white', background: '#6c757d' }}
                                            onClick={e => {
                                                e.stopPropagation();
                                                navigator.clipboard.writeText(JSON.stringify(rebalanceResult, null, 2));
                                                alert('Rebalance data copied to clipboard!');
                                            }}
                                            disabled={!rebalanceResult}
                                        >
                                            <i className="bi bi-clipboard me-2"></i>Copy Data
                                        </button>
                                    </div>
                                    {!isRawCollapsed && (
                                        <div className="scrollable-summary-panel p-0" style={{
                                            maxHeight: '15em', // ~10 lines at 0.8rem line height
                                            minHeight: '3em',
                                            overflowY: 'auto',
                                            borderRadius: '0 0 0.375rem 0.375rem',
                                            width: '100%'
                                        }}>
                                            <pre className="mb-0 px-3 py-2" style={{
                                                whiteSpace: 'pre-wrap',
                                                fontSize: '0.8rem',
                                                lineHeight: '1.4',
                                                fontFamily: 'inherit',
                                                background: 'none',
                                                border: 'none',
                                                padding: 0,
                                                margin: 0,
                                                wordBreak: 'break-word',
                                                overflowWrap: 'break-word',
                                                width: '100%'
                                            }}>
                                                {JSON.stringify(rebalanceResult, null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <p>No rebalance data available</p>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                        >
                            Close
                        </button>
                        {rebalanceResult && !isLoadingPortfolioRebalance && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-success me-2"
                                    onClick={applyRebalanceTrades}
                                    disabled={!rebalanceResult.trades || rebalanceResult.trades.length === 0}
                                >
                                    <i className="bi bi-plus-circle me-2"></i>
                                    Apply Trades to Panel
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PortfolioRebalanceResultsModal;

/* Add this CSS to your main stylesheet (e.g., App.css or index.css):
.scrollable-summary-panel { overflow-y: auto; }
*/
